/**
 * Admin Dashboard - Overview Page
 * Shows key metrics and quick actions
 * Defaults to Real Users Only (excludes test/bot/internal/official/qa)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Users,
  TrendingUp,
  Video,
  Gamepad2,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Clock,
  RefreshCw,
  Eye,
  EyeOff,
  BarChart3
} from 'lucide-react';

const StatCard = ({ title, value, change, changeType, icon: Icon, color, testId }) => {
  const colorClasses = {
    emerald: 'bg-emerald-50 text-emerald-600',
    rose: 'bg-rose-50 text-rose-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1" data-testid={`${testId}-value`}>{value}</p>
          {change !== undefined && (
            <div className="flex items-center gap-1 mt-2">
              {changeType === 'up' ? (
                <ArrowUpRight className="w-4 h-4 text-emerald-500" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-rose-500" />
              )}
              <span className={`text-sm font-medium ${changeType === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                {change}%
              </span>
              <span className="text-xs text-gray-400">vs last week</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { api } = useAdmin();
  const [stats, setStats] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  // Round 36: the Decisions card (what is waiting on a person + today's brief)
  const [decisions, setDecisions] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/api/admin/decisions/brief').then((r) => { if (alive) setDecisions(r.data?.live || null); }).catch(() => {});
    return () => { alive = false; };
  }, [api]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = includeAll ? '?include_all=true' : '';
      const [kpiRes, breakdownRes] = await Promise.all([
        api.get(`/api/admin/kpi/overview${params}`),
        api.get('/api/admin/accounts/breakdown'),
      ]);
      setStats(kpiRes.data);
      setBreakdown(breakdownRes.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Dashboard stats error:', error);
      const msg = error.code === 'ECONNABORTED' ? 'Request timed out' :
                  error.response?.data?.detail || 'Failed to load dashboard stats';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [api, includeAll]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {decisions && (
        <Link to="/decisions" data-testid="dashboard-decisions-card"
          className="block rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-5 py-4 hover:border-emerald-300 transition-colors">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-emerald-800">
                {decisions.queue?.total || 0} decision{(decisions.queue?.total || 0) === 1 ? '' : 's'} waiting on you
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {['content', 'campaigns', 'payouts', 'escalations', 'policy'].map((g) => `${g.charAt(0).toUpperCase() + g.slice(1)} ${decisions.queue?.by_group?.[g] || 0}`).join(' · ')}
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Last 24h: {decisions.numbers?.new_users ?? 0} new · {decisions.numbers?.dau ?? 0} active · {decisions.numbers?.tables_hosted ?? 0} tables
              <span className="ml-2 font-medium text-emerald-700">Open Decisions →</span>
            </p>
          </div>
        </Link>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Welcome to GEMS Admin Panel</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchStats}
            data-testid="refresh-dashboard-btn"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Toggle */}
      <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid="filter-toggle-section">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Data Filter:</span>
        </div>
        <button
          onClick={() => setIncludeAll(false)}
          data-testid="filter-real-only-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            !includeAll
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Eye className="w-4 h-4" />
          Real Users Only
        </button>
        <button
          onClick={() => setIncludeAll(true)}
          data-testid="filter-include-all-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            includeAll
              ? 'bg-gray-700 text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <EyeOff className="w-4 h-4" />
          Include Test / Internal / Bot / Official / QA
        </button>
        {stats?.filter_mode && (
          <span className={`ml-auto text-xs font-medium px-3 py-1 rounded-full ${
            !includeAll ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
          }`} data-testid="filter-mode-badge">
            {!includeAll ? 'Investor-Ready Metrics' : 'All Accounts (Debug)'}
          </span>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.total_users?.toLocaleString() || '0'}
          icon={Users}
          color="emerald"
          testId="stat-total-users"
        />
        <StatCard
          title="Daily Active Users"
          value={stats?.dau?.toLocaleString() || '0'}
          icon={Activity}
          color="blue"
          testId="stat-dau"
        />
        <StatCard
          title="Active Streams"
          value={stats?.active_streams?.toLocaleString() || '0'}
          icon={Video}
          color="rose"
          testId="stat-active-streams"
        />
        <StatCard
          title="Games Today"
          value={stats?.games_today?.toLocaleString() || '0'}
          icon={Gamepad2}
          color="amber"
          testId="stat-games-today"
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Monthly Active Users"
          value={stats?.mau?.toLocaleString() || '0'}
          icon={TrendingUp}
          color="emerald"
          testId="stat-mau"
        />
        <StatCard
          title="New Signups This Week"
          value={stats?.new_signups_week?.toLocaleString() || '0'}
          icon={Users}
          color="blue"
          testId="stat-signups-week"
        />
        <StatCard
          title="Retention Rate"
          value={`${stats?.retention_rate || 0}%`}
          icon={Activity}
          color="rose"
          testId="stat-retention"
        />
      </div>

      {/* Third Row - More Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Overview */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Platform Overview</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-50">
              <span className="text-gray-600">Total Streams Today</span>
              <span className="font-semibold text-gray-900">{stats?.streams_today || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-50">
              <span className="text-gray-600">Total Games Played</span>
              <span className="font-semibold text-gray-900">{stats?.total_games || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-50">
              <span className="text-gray-600">New Signups Today</span>
              <span className="font-semibold text-gray-900" data-testid="signups-today-val">{stats?.new_signups_today || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-50">
              <span className="text-gray-600">Avg Daily Signups (30d)</span>
              <span className="font-semibold text-gray-900" data-testid="avg-daily-signups-val">{stats?.avg_daily_signups || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-gray-600">7-Day Retention</span>
              <span className="font-semibold text-emerald-600">{stats?.retention_rate || 0}%</span>
            </div>
          </div>
        </div>

        {/* Account Type Breakdown */}
        {breakdown && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid="account-breakdown">
            <h3 className="font-semibold text-gray-900 mb-4">Account Classification</h3>
            <div className="space-y-3">
              {Object.entries(breakdown.breakdown || {}).map(([type, count]) => {
                const colors = {
                  real: 'bg-emerald-500',
                  test: 'bg-gray-400',
                  internal: 'bg-blue-500',
                  bot: 'bg-purple-500',
                  qa: 'bg-orange-500',
                  official: 'bg-amber-500',
                };
                const pct = breakdown.total_accounts > 0
                  ? Math.round((count / breakdown.total_accounts) * 100)
                  : 0;
                return (
                  <div key={type} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700 capitalize">{type}</span>
                      <span className="text-gray-500">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${colors[type] || 'bg-gray-400'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="pt-3 mt-3 border-t border-gray-100 flex justify-between text-sm font-semibold">
                <span className="text-gray-900">Total Accounts</span>
                <span className="text-gray-900">{breakdown.total_accounts}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <a href="/admin/users" className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-emerald-50 rounded-lg transition-colors group">
            <Users className="w-6 h-6 text-gray-400 group-hover:text-emerald-600" />
            <span className="text-sm text-gray-600 group-hover:text-emerald-700">View Users</span>
          </a>
          <a href="/admin/streams" className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-rose-50 rounded-lg transition-colors group">
            <Video className="w-6 h-6 text-gray-400 group-hover:text-rose-600" />
            <span className="text-sm text-gray-600 group-hover:text-rose-700">Live Streams</span>
          </a>
          <a href="/admin/moderation" className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-amber-50 rounded-lg transition-colors group">
            <Activity className="w-6 h-6 text-gray-400 group-hover:text-amber-600" />
            <span className="text-sm text-gray-600 group-hover:text-amber-700">Moderation</span>
          </a>
          <a href="/admin/reports" className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors group">
            <TrendingUp className="w-6 h-6 text-gray-400 group-hover:text-blue-600" />
            <span className="text-sm text-gray-600 group-hover:text-blue-700">Reports</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
